import React, {useEffect} from 'react';
import {
  useApolloClient,
  type ApolloClient,
  type NormalizedCacheObject,
} from '@apollo/client';
import * as Sentry from '@sentry/react';

import * as log from './util/log';
import {useBrowserId} from './BrowserId';
import {useSafeNavigate as useNavigate} from './util/safeNavigate';
import {useAppDispatch, useAppSelector} from './state/hooks';
import {reset} from './state/auth';
import {unregisterDevice} from './state/device';

/**
 * Page that performs the logout steps.
 *
 * The UI is transitory, the main point is to stop requests and clean up
 * the local state (cache, store, etc) to unregister the current user.
 */
export const LogoutSplash = () => {
  // Clear logged-in user for error logging.
  Sentry.getCurrentScope().setUser(null);

  const deviceId = useBrowserId();
  const apollo = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userId = useAppSelector((state) => state.auth.userId!);

  // Do all the unregistration stuff.
  const unregister = async () => {
    try {
      try {
        await dispatch(unregisterDevice({client: apollo, userId, deviceId}));
      } catch (e) {
        log.error(`Error unregistering device: ${e}`);
      }
      await apollo.stop();
      await apollo.clearStore();
      await dispatch(reset());
    } catch (e) {
      log.error(`Error logging out: ${e}`);
    } finally {
      navigate('/login');
    }
  };

  useEffect(() => {
    unregister();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="w-full max-w-md space-y-6 text-center">
        <div>
          <h2 className="text-3xl font-bold mb-4">Logging you out ...</h2>
          <p className="text-muted-foreground">
            You should be redirected to the login screen momentarily. Click{' '}
            <a href="/login" className="text-primary hover:underline">
              here
            </a>{' '}
            if you don't get redirected automatically.
          </p>
        </div>
      </div>
    </div>
  );
};
